import React, { useState, useReducer, Reducer } from "react";
import { useDebounce } from "react-use";
import { useLocation, useNavigate } from "react-router-dom";
import { useNavigationContext } from "../../../contexts/navigationContext";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { FontIcon } from "@fluentui/react";
import { Button, Dialog, DialogBody, DialogSurface, DialogTitle, MessageBar, MessageBarActions, MessageBarBody, ProgressBar } from "@fluentui/react-components";
import { useQuery, useMutation } from "@tanstack/react-query";

import { priorityContacts, PriorityContactsAction, PriorityContactsState } from "../../../types/priorityContacts";
import { PriorityContactsSelector } from "./PriorityContactsSelector";
import {
  getPriorityContacts,
  setPriorityContacts,
  getWorkspaces,
} from "../../../api/priorityContacts";
import { pauseNotification } from "../../../api/pause";
import { getGraphPeople, searchGraphPeople } from "../../../api/people";
import FullPageLoader from "../../../components/loaders/FullPageLoader";
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt";
import { iconButtonClass, modalStyles, pickerStyle, saveBtnClass, progressBarClass, progressBarWrapperClass, useDialogTitleStyles, scrollContainerClass, infoBarClass } from "./styles";
import { mapResponsePeople, mapResponseWorkspace } from "./utils";
import useMessageContext from "../../../hooks/useMessageContext";
import useAuthContext from "../../../hooks/useAuthContext";
import { NO_DOC_MANAGEMENT } from "../../../config";
import PageHeader from "../../../components/Headings/PageHeader";
import { PeopleRegular, DismissRegular } from "@fluentui/react-icons";


const PriorityContacts = () => {
  const { handleShowMessageBar } = useMessageContext();
  const appInsights = useAppInsightsContext();
  const { tokenData, appInfo } = useAuthContext();
  const msUserId = tokenData.oid;

  const [showInfoBar, setShowInfoBar] = useState(true);
  const [filter, setFilter] = useState("");
  const [debouncedFilterValue, setDebouncedFilterValue] = useState("");
  const { state: locationState }: any = useLocation();
  const navigate = useNavigate();
  const { setFromPage } = useNavigationContext();
  const [{ isFormChanged, pickedPeople, pickedWorkspaces, returnedPeople, favourites, returnedWorkspaces }, dispatch] =
  useReducer((state: PriorityContactsState, action: PriorityContactsAction) =>  {
        switch (action.type) {
          case "SET_INITIAL_SELECTED_DATA":
            return {
              ...state,
              pickedPeople: action.payload.pickedPeople,
              pickedWorkspaces: action.payload.pickedWorkspaces,
            };

          case "SET_RETURNED_PEOPLE":
            return {
              ...state,
              returnedPeople: action.payload.returnedPeople,
              favourites: action.payload.favourites,
            };

          case "SET_RETURNED_WORKSPACES":
            return {
              ...state,
              returnedWorkspaces: action.payload,
            };
          case "SET_FORM_CHANGED":
            return {
              ...state,
              isFormChanged: true,
            };
          case "SET_FORM_CHANGED_SUCCESS":
            return {
              ...state,
              isFormChanged: false,
            };
          case "UPDATE_PICKED_PEOPLE":
            return {
              ...state,
              pickedPeople: action.payload,
              isFormChanged: true,
            };
          case "UPDATE_PICKED_WORKSPACES":
            return {
              ...state,
              pickedWorkspaces: action.payload,
              isFormChanged: true,
            };
          case "UPDATE_RETURNED_PEOPLE":
            return {
              ...state,
              returnedPeople: action.payload,
            };
        }

        return state;
      },
      {
        isFormChanged: false,
        userAction: false,
        pickedPeople: [],
        pickedWorkspaces: [],
        returnedPeople: [],
        favourites: [],
        returnedWorkspaces: [],
      }
    );

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(isFormChanged);

  useDebounce(
    () => {
      setDebouncedFilterValue(filter);
    },
    // delay set to 0.5s to avoid too many api calls
    // Note:  So that control updates suggestions consistent with API results,  this must be at least 400ms under the resolveDelay prop in the ListPeoplePicker component in PriortityContactsSelector.tsx
    500,
    [filter]
  );

  // Search people query - v5 uses select instead of onSuccess
  const { data: searchPeopleData } = useQuery({
    queryKey: ["filterPeople", msUserId, debouncedFilterValue, locationState],
    queryFn: () => searchGraphPeople(msUserId, debouncedFilterValue),
    enabled: !!debouncedFilterValue,
    select: (response) => mapResponsePeople(response),
  });

  // Update state when search results change
  React.useEffect(() => {
    if (searchPeopleData) {
      dispatch({
        type: "UPDATE_RETURNED_PEOPLE",
        payload: searchPeopleData,
      });
    }
  }, [searchPeopleData]);

  // Get priority contacts - v5 uses select instead of onSuccess
  const { data, status, isPending: isContactsPending } = useQuery({
    queryKey: ["priorityContacts", msUserId, locationState],
    queryFn: () => getPriorityContacts(msUserId),
    enabled: !!msUserId,
  });

  // Update initial data when contacts are loaded
  React.useEffect(() => {
    if (data) {
      dispatch({
        type: "SET_INITIAL_SELECTED_DATA",
        payload: {
          pickedPeople: data.people,
          pickedWorkspaces: data.workspaces,
        },
      });
    }
  }, [data]);

  // Get graph people - v5 uses select instead of onSuccess
  const { status: graphStatus, data: graphPeopleData, isPending: isGraphPending } = useQuery({
    queryKey: ["getGraphPeople", msUserId, locationState],
    queryFn: () => getGraphPeople(msUserId),
    select: (response) => {
      const responseFavourites = response.filter((person) => person.isFavorite);
      const favouritesToSet = responseFavourites.length > 0 ? responseFavourites : response.slice(0, 5);

      return {
        returnedPeople: mapResponsePeople(response),
        favourites: mapResponsePeople(favouritesToSet),
      };
    },
  });

  // Update state when graph people data changes
  React.useEffect(() => {
    if (graphPeopleData) {
      dispatch({
        type: "SET_RETURNED_PEOPLE",
        payload: graphPeopleData,
      });
    }
  }, [graphPeopleData]);

  const duration = locationState.duration;
  const { mutateAsync: pauseMutate } = useMutation({
    mutationFn: async () => pauseNotification(duration, msUserId),
      onSuccess() {
        if (duration !== "9999999") {
          const durationInMs = Number.parseFloat(duration) * 60000;
          const offDate = new Date().getTime();
          const endDate = new Date(offDate + durationInMs);
          let hours = endDate.getHours();
          const minutes = endDate.getMinutes().toString().padStart(2, "0");
          const ampm = hours >= 12 ? 'pm' : 'am'
          hours = hours % 12;
          hours = hours ? hours : 12; // the hour '0' should be '12'

          const today = new Date();
          const tomorrow = new Date();
          tomorrow.setDate(today.getDate() + 1);

          let date;
          if (endDate.toDateString() === today.toDateString()) {
            date = 'today';
          } else if (endDate.toDateString() === tomorrow.toDateString()) {
            date = 'tomorrow';
          } else {
            date = endDate.toLocaleDateString('en-GB');
          }

          const dateFormatted = `${hours}:${minutes} ${ampm} ${date}`;
          handleShowMessageBar(`Incoming emails are paused until ${dateFormatted}`, false);

        } else {
          handleShowMessageBar(`Incoming emails are paused`, false);
        }
        const currentPath = location.pathname;
        navigate(-1);
        setTimeout(() => {
          if (location.pathname === currentPath) {
            setFromPage(true);
            navigate("/");
          }
        }, 100);
      },
    }
  );

  const selectedPeople: priorityContacts = {
    people: pickedPeople.map((item) => {
      return {
        displayName: item.displayName || item.text,
        address: item.address || item.secondaryText,
      };
    }),
    workspaces: pickedWorkspaces.map((item) => {
      return {
        workspaceId: item.workspaceId || item.optionalText,
        workspaceName: item.workspaceName || item.primaryText,
        database: item.database || item.tertiaryText,
      };
    }),
  };

  // Save priority contacts mutation - v5 uses onMutate and onSuccess
  const { mutate: savePriorityContacts, status: contactsLoadingStatus, isPending: isSaving } = useMutation({
    mutationFn: () => setPriorityContacts(msUserId, selectedPeople),
    onMutate: () => {
      appInsights.trackEvent({ name: "Save priority contacts" });
    },
    onSuccess: () => {
      dispatch({
        type: "SET_FORM_CHANGED_SUCCESS",
      });
    },
  });

  // Get workspaces - v5 uses select instead of onSuccess
  const { status: workspacesStatus, fetchStatus, data: workspacesData, isPending: isWorkspacesPending } = useQuery({
    queryKey: ["getWorkspaces", msUserId, locationState],
    queryFn: () => getWorkspaces(msUserId),
    enabled: !!msUserId && appInfo.documentManagement !== NO_DOC_MANAGEMENT,
    select: (response) => mapResponseWorkspace(response),
  });

  // Update state when workspaces data changes
  React.useEffect(() => {
    if (workspacesData) {
      dispatch({
        type: "SET_RETURNED_WORKSPACES",
        payload: workspacesData,
      });
    }
  }, [workspacesData]);

  // v5 uses isPending instead of status === "loading"
  const isContactsLoading = isSaving; 
  const disableWorkspacePicker = isContactsLoading || workspacesStatus === "error";
const workspacePlacerText = workspacesStatus === "error" ? "Workspaces unavailable" : "Search for a workspace";

  const pickedPeopleChangeHadler = (value) => {
    dispatch({
      type: "UPDATE_PICKED_PEOPLE",
      payload: value,
    });

    return value;
  };

  const workspaceChangeHadler = (value) => {
    dispatch({
      type: "UPDATE_PICKED_WORKSPACES",
      payload: value,
    });

    return value;
  };

  const peopleFilterHandler = (val) => {
    setFilter(val);
    return val;
  };

  const workspaceFilterHandler = (val) => {
    return val;
  };

  const onSavePress = () => {
    savePriorityContacts();
  };

  const handlePauseClick = async () => {
    const hasSavedWorkspaces = pickedWorkspaces.length > 0;
    const shouldSaveContacts = (hasSavedWorkspaces || isFormChanged);

    if (shouldSaveContacts) {
      savePriorityContacts();
    }
    const data = await pauseMutate();
    if (data) {
      dispatch({
        type: "SET_FORM_CHANGED_SUCCESS",
      });
    }
  };
const styles = useDialogTitleStyles();
  const onConfirmButton = () => {
    savePriorityContacts();
    confirmNavigation();
  };

  // v5: Use isPending instead of status === "loading"
  if (isContactsPending || isGraphPending || (isWorkspacesPending && fetchStatus !== "idle")) {
    return <FullPageLoader title="Contacts loading..." />;
  }

  return (
    <div className={scrollContainerClass}>
      <PageHeader headingText={locationState ? locationState.title : "Priority Contacts"} icon={<PeopleRegular />} />
      <div className={pickerStyle}>
        <PriorityContactsSelector
          people={returnedPeople}
          searchTarget="people"
          onInputChange={peopleFilterHandler}
          favourites={favourites}
          defaultSelectedItems={mapResponsePeople(data?.people || [])}
          onChange={pickedPeopleChangeHadler}
          disabled={isContactsLoading}
          placeholder="Search for a name"
        />
      </div>
      
      {appInfo.documentManagement !== NO_DOC_MANAGEMENT && (
        <div className={pickerStyle}>
          <PriorityContactsSelector
            people={returnedWorkspaces}
            searchTarget="workspaces"
            onInputChange={workspaceFilterHandler}
            favourites={returnedWorkspaces}
            defaultSelectedItems={mapResponseWorkspace(data?.workspaces || [])}
            onChange={workspaceChangeHadler}
            disabled={disableWorkspacePicker}
            placeholder={workspacePlacerText}
          />
        </div>
      )}

      <div className={progressBarWrapperClass}>
        {isContactsLoading && <ProgressBar className={progressBarClass} />}
      </div>

      {locationState.buttonText === "Save" ? (
        <Button appearance="primary" onClick={onSavePress} disabled={!isFormChanged} className={saveBtnClass}>
          {locationState ? locationState.buttonText : "Save"}
        </Button>
      ) : (
        <Button appearance="primary" onClick={handlePauseClick} className={saveBtnClass}>
          {locationState.buttonText}
        </Button>
      )}
      {showInfoBar && (
        <MessageBar intent="info" className={infoBarClass}>
          <MessageBarBody>
            When emails are paused, you will still receive emails from priority people {appInfo.documentManagement !== NO_DOC_MANAGEMENT && (<span>and matters </span>)} saved here.
          </MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button appearance="transparent" icon={<DismissRegular />} aria-label="Dismiss" onClick={() => setShowInfoBar(false)} />
            }
          />
        </MessageBar>
      )}
      <Dialog
        open={showPrompt}
        onOpenChange={(_, data) => { if (!data.open) cancelNavigation(); }}
      >
        <DialogSurface className={modalStyles.container}>
          <DialogBody>

  <DialogTitle 
    as="h3" className={styles.title}
    action={
      <Button
        appearance="subtle"
        icon={<FontIcon iconName="Cancel" />}
        aria-label="Close popup"
        onClick={cancelNavigation}
      />
    }
  >
    Save changes made to Priority Contacts?
  </DialogTitle>

            <div className={modalStyles.body}>
              <Button appearance="primary" onClick={onConfirmButton} className={modalStyles.btnClass}>
                Yes
              </Button>
              <Button onClick={confirmNavigation} className={modalStyles.btnClass}>
                No
              </Button>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default PriorityContacts;